/**
 * ReceiptPreview - Renders a customer receipt for printing or preview
 * Supports Star TSP thermal printer format
 */
import { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { RefreshCw } from 'lucide-react';
import { api } from '@/utils/apiClient';

// Escape HTML to prevent XSS
const escapeHtml = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

export default function ReceiptPreview({ orderId, isReprint = false, onClose }) {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoLoadError, setLogoLoadError] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    fetchReceipt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchReceipt = async () => {
    try {
      const res = await api.get(`/receipts/customer/${orderId}?is_reprint=${isReprint}`);
      setReceipt(res.data);
    } catch (err) {
      console.error('Failed to fetch receipt');
    }
    setLoading(false);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    // Get sanitized innerHTML (React already escapes content)
    const safeContent = printContent.innerHTML;
    const safeOrderNumber = escapeHtml(receipt?.order_number);
    
    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${safeOrderNumber}</title>
          <style>
            @page { 
              size: 80mm auto; 
              margin: 0; 
            }
            body { 
              font-family: 'Courier New', monospace;
              font-size: 12px;
              width: 80mm;
              margin: 0;
              padding: 8mm;
              background: white;
              color: black;
            }
            .receipt-container {
              width: 100%;
            }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .font-bold { font-weight: bold; }
            .text-lg { font-size: 14px; }
            .text-sm { font-size: 10px; }
            .text-xs { font-size: 9px; }
            .border-dashed {
              border-bottom: 1px dashed #000;
              padding-bottom: 8px;
              margin-bottom: 8px;
            }
            .flex-between {
              display: flex;
              justify-content: space-between;
            }
            .mb-1 { margin-bottom: 4px; }
            .mb-2 { margin-bottom: 8px; }
            .mt-2 { margin-top: 8px; }
            .reprint-banner {
              background: #000;
              color: #fff;
              padding: 4px;
              text-align: center;
              font-weight: bold;
              margin-bottom: 8px;
            }
            .qr-code {
              display: flex;
              justify-content: center;
              margin: 8px 0;
            }
            /* Logo with white background container */
            .logo-container {
              display: flex;
              justify-content: center;
              margin: 8px 0;
            }
            .logo-wrapper {
              background: white;
              padding: 8px;
              border-radius: 4px;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }
            .logo-wrapper img {
              height: 48px;
              width: auto;
              max-width: 120px;
              object-fit: contain;
            }
            /* Kiosk/Paid markers */
            .kiosk-marker {
              background: #dbeafe;
              color: #1e40af;
              font-weight: bold;
              padding: 4px 8px;
              margin: 4px 0;
              border-radius: 4px;
              text-align: center;
            }
            .paid-marker {
              background: #dcfce7;
              color: #166534;
              font-weight: bold;
              padding: 4px 8px;
              margin: 4px 0;
              border-radius: 4px;
              text-align: center;
            }
          </style>
        </head>
        <body>
          ${safeContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!receipt) {
    return <div className="text-center py-8 text-muted-foreground">Receipt not found</div>;
  }

  return (
    <div className="space-y-4">
      {/* Print Preview */}
      <div 
        ref={printRef}
        className="bg-white text-black p-6 rounded-lg shadow-lg max-w-[300px] mx-auto font-mono text-xs"
        data-testid="receipt-preview"
      >
        {/* Reprint Banner */}
        {receipt.is_reprint && (
          <div className="reprint-banner bg-black text-white p-1 text-center font-bold mb-4">
            *** REPRINT ***
          </div>
        )}

        {/* Header */}
        <div className="text-center border-b border-dashed border-gray-400 pb-4 mb-4">
          <p className="font-bold text-sm">*** TAX INVOICE ***</p>
          
          {/* Kiosk/Paid Markers */}
          {receipt.is_kiosk_order && (
            <div className="bg-blue-100 text-blue-800 font-bold py-1 px-2 my-2 rounded">
              KIOSK ORDER
            </div>
          )}
          {receipt.is_paid && receipt.is_kiosk_order && (
            <div className="bg-green-100 text-green-800 font-bold py-1 px-2 mb-2 rounded">
              PAID
            </div>
          )}
          
          <p className="font-bold mt-2">{receipt.company_name}</p>
          <p>{receipt.company_address_line1}</p>
          <p>{receipt.company_address_line2}</p>
          <p className="mt-1">TIN: {receipt.tin}</p>
        </div>

        {/* Transaction Info */}
        <div className="border-b border-dashed border-gray-400 pb-4 mb-4">
          <div className="flex justify-between mb-1">
            <span>Transaction:</span>
            <span>{receipt.transaction_number}</span>
          </div>
          <div className="flex justify-between mb-1">
            <span>Order #:</span>
            <span>{receipt.order_number}</span>
          </div>
          <div className="flex justify-between mb-1">
            <span>Date:</span>
            <span>{new Date(receipt.date).toLocaleDateString()}</span>
          </div>
          <div className="flex justify-between">
            <span>Time:</span>
            <span>{receipt.time}</span>
          </div>
          {receipt.customer_name && receipt.customer_name !== 'Guest' && (
            <div className="flex justify-between mt-1">
              <span>Customer:</span>
              <span>{receipt.customer_name}</span>
            </div>
          )}
        </div>

        {/* Items */}
        <div className="border-b border-dashed border-gray-400 pb-4 mb-4">
          {receipt.items.map((item, idx) => (
            <div key={idx} className="mb-2">
              <div className="flex justify-between">
                <span>{item.quantity}x {item.name}{item.size ? ` (${item.size})` : ''}</span>
                <span>${item.total_price.toFixed(2)}</span>
              </div>
              {item.customizations && item.customizations.length > 0 && (
                <div className="ml-4 text-[10px] text-gray-600">
                  {item.customizations.map((c, i) => (
                    <div key={i}>+ {typeof c === 'object' ? c.name : c}</div>
                  ))}
                </div>
              )}
              {item.special_instructions && (
                <div className="ml-4 text-[10px] text-gray-600 italic">
                  Note: {item.special_instructions}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="border-b border-dashed border-gray-400 pb-4 mb-4">
          <div className="flex justify-between mb-1">
            <span>Subtotal:</span>
            <span>${receipt.subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between mb-1">
            <span>VAT ({receipt.vat_rate}%):</span>
            <span>${receipt.vat_amount.toFixed(2)}</span>
          </div>
          {receipt.discount > 0 && (
            <div className="flex justify-between mb-1 text-green-700">
              <span>Discount{receipt.promo_code ? ` (${receipt.promo_code})` : ''}:</span>
              <span>-${receipt.discount.toFixed(2)}</span>
            </div>
          )}
          {receipt.is_comped && (
            <div className="flex justify-between mb-1 text-red-700 font-bold">
              <span>*** COMPED ***</span>
              <span>$0.00</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-sm mt-2 pt-2 border-t border-gray-300">
            <span>TOTAL:</span>
            <span>${receipt.is_comped ? '0.00' : receipt.total.toFixed(2)}</span>
          </div>
        </div>

        {/* Payment */}
        <div className="border-b border-dashed border-gray-400 pb-4 mb-4">
          <div className="flex justify-between">
            <span>Payment Method:</span>
            <span className="uppercase">{receipt.payment_method}</span>
          </div>
          <div className="flex justify-between">
            <span>Status:</span>
            <span className="uppercase">{receipt.payment_status}</span>
          </div>
        </div>

        {/* QR Code & Footer */}
        <div className="text-center">
          <div className="flex justify-center my-3">
            <QRCodeSVG 
              value={receipt.loyalty_signup_url} 
              size={80}
              level="M"
            />
          </div>
          <p className="text-[10px] mb-4">Scan to join our rewards program!</p>
          <p className="font-bold text-sm">{receipt.footer_message}</p>
          
          {/* Logo at bottom - ONLY for customer receipts, NOT station tickets */}
          {receipt.receipt_type === 'customer' && receipt.logo_url && receipt.print_logo_on_receipt !== false && (
            <div className="flex justify-center mt-4 pt-4 border-t border-dashed border-gray-300">
              <div className="bg-white p-3 rounded-lg shadow-sm">
                {!logoLoadError ? (
                  <img 
                    src={receipt.logo_url} 
                    alt="Bulaccino Cafe" 
                    className="h-16 w-auto object-contain"
                    style={{ maxWidth: '150px' }}
                    onError={() => setLogoLoadError(true)}
                  />
                ) : (
                  <p className="text-red-500 text-xs text-center">Logo unavailable</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Print Button */}
      <div className="flex justify-center gap-3">
        <button
          onClick={handlePrint}
          className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90"
          data-testid="print-receipt-btn"
        >
          Print Receipt
        </button>
        {onClose && (
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
}
